import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  EmailContent,
  PrefillEmail,
  SupportEmailContext,
} from '../models/prefill-email';
import {
  buildMailtoUrl,
  renderEmailHtml,
  renderEmailPlainText,
} from '../utils/email-body.util';
import { OutlookService } from './outlook/outlook.service';

const SUPPORT_EMAIL_RECIPIENT = 'support@maximizer.com';

@Injectable()
export class EmailBuilderService {
  private readonly translate = inject(TranslateService);
  private readonly outlookService = inject(OutlookService);

  buildSupportEmail(context: SupportEmailContext): PrefillEmail {
    const notAvailable = this.translate.instant(
      'outlook.prefillEmail.common.notAvailable',
    );
    const pleaseSpecify = this.translate.instant(
      'outlook.prefillEmail.common.pleaseSpecify',
    );
    const support = (key: string): string =>
      this.translate.instant(`outlook.prefillEmail.support.${key}`);

    const content: EmailContent = {
      greeting: support('greeting'),
      paragraphs: [support('intro')],
      sections: [
        {
          heading: support('issueDetailsHeading'),
          items: [
            { label: support('issueStartLabel'), value: pleaseSpecify },
            { label: support('reauthLabel'), value: pleaseSpecify },
            {
              label: support('syncTypeLabel'),
              value: support('syncTypePlaceholder'),
            },
          ],
        },
        {
          heading: support('userDetailsHeading'),
          items: [
            {
              label: support('userIdLabel'),
              value: this.valueOrPlaceholder(context.userId, notAvailable),
            },
            {
              label: support('accountLabel'),
              value: this.valueOrPlaceholder(context.account, notAvailable),
            },
            {
              label: support('emailLabel'),
              value: this.valueOrPlaceholder(context.email, pleaseSpecify),
            },
          ],
        },
        {
          heading: support('environmentHeading'),
          items: [
            {
              label: support('platformLabel'),
              value: this.valueOrPlaceholder(context.platform, pleaseSpecify),
            },
            {
              label: support('outlookVersionLabel'),
              value: this.valueOrPlaceholder(
                context.outlookVersion,
                notAvailable,
              ),
            },
            {
              label: support('browserLabel'),
              value: this.valueOrPlaceholder(context.browser, pleaseSpecify),
            },
            {
              label: support('osLabel'),
              value: this.valueOrPlaceholder(context.os, pleaseSpecify),
            },
            {
              label: support('appVersionLabel'),
              value: this.valueOrPlaceholder(context.appVersion, notAvailable),
            },
          ],
        },
      ],
    };

    return {
      toRecipients: [SUPPORT_EMAIL_RECIPIENT],
      subject: support('subject'),
      htmlBody: renderEmailHtml(content),
      plainTextBody: renderEmailPlainText(content),
    };
  }

  open(email: PrefillEmail): void {
    const opened = this.outlookService.createNewMessage({
      toRecipients: email.toRecipients,
      subject: email.subject,
      htmlBody: email.htmlBody,
    });

    if (opened) {
      return;
    }

    globalThis.location.href = buildMailtoUrl(email);
  }

  private valueOrPlaceholder(
    value: string | undefined,
    placeholder: string,
  ): string {
    return value?.trim() ? value.trim() : placeholder;
  }
}
